import React, { useState, useEffect } from 'react';
import { unitsApi, driversApi, dispatchersApi, ownerOperatorsApi } from '../lib/api';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { 
  Truck, 
  Plus, 
  Pencil, 
  Trash2,
  Search,
  User,
  UserPlus,
  AlertCircle,
  Container,
  Sparkles,
  Upload,
  Loader2,
  RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import TrailersTab from './TrailersTab';

export default function Units() {
  const { canEdit, isAdmin } = useAuth();
  const [units, setUnits] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  // Status filter — Active by default. User wanted a quick toggle to see only
  // active units (most common), only deactivated ones, or both.
  const [statusFilter, setStatusFilter] = useState('active');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  // AI-parse of IRP / vehicle registration
  const [aiParsing, setAiParsing] = useState(false);
  const [aiFileRef, setAiFileRef] = useState(null); // {file_id, filename}
  const [formData, setFormData] = useState({
    unit_number: '',
    make: '',
    model: '',
    year: '',
    vin: '',
    license_plate: '',
    status: 'active',
    inactive_since: '',
    assigned_driver_id: 'none',
    assigned_dispatcher_id: 'none',
    assigned_owner_operator_id: 'none',
    dispatch_fee_type: 'default',
    dispatch_fee_value: '',
  });

  const [dispatchers, setDispatchers] = useState([]);
  const [ownerOps, setOwnerOps] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [unitsRes, driversRes, dispRes, ooRes] = await Promise.all([
        unitsApi.getAll(),
        driversApi.getAll(),
        dispatchersApi.getAll().catch(() => ({ data: [] })),
        ownerOperatorsApi.getAll().catch(() => ({ data: [] })),
      ]);
      setUnits(unitsRes.data);
      setDrivers(driversRes.data);
      setDispatchers(dispRes.data || []);
      setOwnerOps(ooRes.data || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Get available (unassigned) drivers
  const getAvailableDrivers = () => {
    const assignedDriverIds = units
      .filter(u => u.assigned_driver_id && u.id !== editingUnit?.id)
      .map(u => u.assigned_driver_id);
    return drivers.filter(d => !assignedDriverIds.includes(d.id) && d.status === 'active');
  };

  const handleAiParseRegistration = async (file) => {
    if (!file) return;
    setAiParsing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('doc_type', 'irp_registration');
      const res = await api.post('/ai/parse-document', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90000,
      });
      const parsed = res.data?.parsed || {};
      setAiFileRef({ file_id: res.data?.file_id, filename: res.data?.filename });
      setFormData((prev) => ({
        ...prev,
        unit_number: parsed.unit_number || prev.unit_number,
        make: parsed.make || prev.make,
        model: parsed.model || prev.model,
        year: parsed.year ? String(parsed.year) : prev.year,
        vin: parsed.vin || prev.vin,
        license_plate: parsed.license_plate || prev.license_plate,
      }));
      if (parsed.vin || parsed.unit_number || parsed.make) {
        toast.success('Registration extracted — review and save');
      } else {
        toast.info('File uploaded, but no fields were extracted. Please fill them in.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Extract failed');
    } finally {
      setAiParsing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        year: formData.year ? parseInt(formData.year) : null,
        // Clear inactive_since once a unit is marked active again — keeps the
        // unit visible from "today" onward without leftover off-date metadata.
        inactive_since: formData.status === 'active' ? null : (formData.inactive_since || null),
        assigned_driver_id: formData.assigned_driver_id === 'none' ? null : formData.assigned_driver_id,
        assigned_dispatcher_id: formData.assigned_dispatcher_id === 'none' ? null : formData.assigned_dispatcher_id,
        assigned_owner_operator_id: formData.assigned_owner_operator_id === 'none' ? null : formData.assigned_owner_operator_id,
        dispatch_fee_type: formData.dispatch_fee_type === 'default' ? null : formData.dispatch_fee_type,
        dispatch_fee_value: formData.dispatch_fee_type === 'default' ? null : (formData.dispatch_fee_value ? parseFloat(formData.dispatch_fee_value) : null),
      };

      // Force the user to choose an inactive date when deactivating — otherwise
      // recurring deductions and auto-charges wouldn't know when to stop.
      if (data.status !== 'active' && !data.inactive_since) {
        toast.error('Please pick the date the unit went inactive');
        return;
      }

      if (editingUnit) {
        await unitsApi.update(editingUnit.id, data);
        toast.success('Unit updated successfully');
      } else {
        const res = await unitsApi.create(data);
        const newUnitId = res?.data?.id;
        toast.success('Unit created successfully');

        // If an IRP registration was AI-parsed, attach it to this unit as a
        // vehicle_record so it shows up contextually under Vehicle Mgmt.
        if (aiFileRef?.file_id && newUnitId) {
          try {
            await api.post('/vehicle-records', {
              unit_id: newUnitId,
              record_type: 'registration',
              title: 'IRP / Vehicle Registration',
              description: `Registration parsed via AI from ${aiFileRef.filename}`,
              date: new Date().toISOString().slice(0, 10),
              file_ids: [aiFileRef.file_id],
            });
            toast.success('Registration saved under this unit');
          } catch (err) {
            console.error('Failed to attach registration file:', err);
          }
        }
      }
      setDialogOpen(false);
      resetForm();
      setAiFileRef(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (unit) => {
    setEditingUnit(unit);
    setFormData({
      unit_number: unit.unit_number,
      make: unit.make || '',
      model: unit.model || '',
      year: unit.year?.toString() || '',
      vin: unit.vin || '',
      license_plate: unit.license_plate || '',
      status: unit.status,
      inactive_since: unit.inactive_since || '',
      assigned_driver_id: unit.assigned_driver_id || 'none',
      assigned_dispatcher_id: unit.assigned_dispatcher_id || 'none',
      assigned_owner_operator_id: unit.assigned_owner_operator_id || 'none',
      dispatch_fee_type: unit.dispatch_fee_type || 'default',
      dispatch_fee_value: unit.dispatch_fee_value?.toString() || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this unit?')) return;
    try {
      await unitsApi.delete(id);
      toast.success('Unit deleted successfully');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  // Quick assign driver without opening full edit dialog
  const handleQuickAssign = async (unitId, driverId) => {
    try {
      const unit = units.find(u => u.id === unitId);
      await unitsApi.update(unitId, {
        ...unit,
        assigned_driver_id: driverId === 'none' ? null : driverId,
      });
      toast.success('Driver assigned successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to assign driver');
    }
  };

  const handleSetUnitStatus = async (unit, status) => {
    if (status !== 'active' && !window.confirm(`Mark unit ${unit.unit_number} inactive as of today? Its history stays intact.`)) return;
    try {
      const res = await unitsApi.setStatus(unit.id, status);
      setUnits((prev) => prev.map((u) => (u.id === unit.id ? { ...u, status, inactive_since: res.data?.inactive_since || null } : u)));
      toast.success(`Unit ${unit.unit_number} ${status === 'active' ? 'reactivated' : 'marked inactive'}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const resetForm = () => {
    setEditingUnit(null);
    setFormData({
      unit_number: '',
      make: '',
      model: '',
      year: '',
      vin: '',
      license_plate: '',
      status: 'active',
      inactive_since: '',
      assigned_driver_id: 'none',
      assigned_dispatcher_id: 'none',
      assigned_owner_operator_id: 'none',
      dispatch_fee_type: 'default',
      dispatch_fee_value: '',
    });
  };

  const filteredUnits = units.filter(unit => {
    if (statusFilter === 'active' && unit.status !== 'active') return false;
    if (statusFilter === 'inactive' && unit.status === 'active') return false;
    const q = searchTerm.toLowerCase();
    if (!q) return true;
    return (
      unit.unit_number.toLowerCase().includes(q) ||
      unit.make?.toLowerCase().includes(q) ||
      unit.model?.toLowerCase().includes(q)
    );
  });

  const statusColors = {
    active: 'bg-emerald-100 text-emerald-700',
    inactive: 'bg-slate-100 text-slate-700',
    maintenance: 'bg-amber-100 text-amber-700',
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="units-loading">
        <Skeleton className="h-8 w-48" />
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="units-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-manrope font-bold text-foreground">Fleet</h1>
        <p className="text-muted-foreground mt-1">Manage your trucks and trailers</p>
      </div>

      <Tabs defaultValue="units" className="space-y-4">
        <TabsList className="grid w-full max-w-[360px] grid-cols-2">
          <TabsTrigger value="units" data-testid="tab-units">
            <Truck className="h-3.5 w-3.5 mr-1.5" />Units
          </TabsTrigger>
          <TabsTrigger value="trailers" data-testid="tab-trailers">
            <Container className="h-3.5 w-3.5 mr-1.5" />Trailers
          </TabsTrigger>
        </TabsList>

        <TabsContent value="units" className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-4">
        {canEdit && (
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-unit-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Unit
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle className="font-manrope">
                  {editingUnit ? 'Edit Unit' : 'Add New Unit'}
                </DialogTitle>
                <DialogDescription>
                  {editingUnit ? 'Update unit details' : 'Add a new truck to your fleet'}
                </DialogDescription>
              </DialogHeader>
              {!editingUnit && (
                <div
                  className="rounded-lg border-2 border-dashed border-blue-300 dark:border-blue-700 bg-blue-50/70 dark:bg-blue-950/30 p-3 space-y-2 transition-colors"
                  onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-blue-500', 'bg-blue-100'); }}
                  onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove('border-blue-500', 'bg-blue-100'); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('border-blue-500', 'bg-blue-100');
                    const f = e.dataTransfer.files?.[0];
                    if (f) handleAiParseRegistration(f);
                  }}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 text-sm font-medium text-blue-900 dark:text-blue-100">
                      <Sparkles className="h-4 w-4 text-blue-600" />
                      Extract from Registration or Cab Card
                    </div>
                    {aiFileRef && (
                      <span className="text-[11px] text-blue-700 dark:text-blue-300 truncate max-w-[180px]" title={aiFileRef.filename}>
                        {aiFileRef.filename}
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-blue-800/80 dark:text-blue-200/80">
                    Drag &amp; drop or click to upload a photo/PDF of the IRP cab card, vehicle registration, or title.
                    VIN, unit number, make, year, and plate are extracted automatically. The file is saved under this
                    unit's documents.
                  </p>
                  <label className="inline-flex">
                    <input
                      type="file"
                      className="hidden"
                      accept="image/png,image/jpeg,image/webp,application/pdf"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleAiParseRegistration(f);
                        e.target.value = '';
                      }}
                      data-testid="ai-parse-reg-input"
                    />
                    <span
                      className={`inline-flex items-center gap-2 text-xs font-medium rounded-md border border-blue-300 dark:border-blue-700 bg-white dark:bg-slate-900 px-3 py-1.5 cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/40 ${aiParsing ? 'opacity-60 pointer-events-none' : ''}`}
                      data-testid="ai-parse-reg-btn"
                    >
                      {aiParsing ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Upload className="h-3.5 w-3.5" />
                      )}
                      {aiParsing ? 'Extracting…' : 'Upload or Drop File'}
                    </span>
                  </label>
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="unit_number">Unit Number *</Label>
                    <Input
                      id="unit_number"
                      value={formData.unit_number}
                      onChange={(e) => setFormData({ ...formData, unit_number: e.target.value })}
                      placeholder="e.g., TRK-001"
                      required
                      data-testid="unit-number-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="status">Status</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(value) => setFormData({
                        ...formData,
                        status: value,
                        // Default the inactive date to today when the user flips to inactive
                        // (they can still change it). Clear it when going back to active.
                        inactive_since: value === 'active'
                          ? ''
                          : (formData.inactive_since || new Date().toISOString().split('T')[0]),
                      })}
                    >
                      <SelectTrigger data-testid="unit-status-select">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                        <SelectItem value="maintenance">Maintenance</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {formData.status !== 'active' && (
                  <div className="space-y-2">
                    <Label htmlFor="inactive_since">Inactive Since *</Label>
                    <Input
                      id="inactive_since"
                      type="date"
                      value={formData.inactive_since}
                      onChange={(e) => setFormData({ ...formData, inactive_since: e.target.value })}
                      required
                      data-testid="unit-inactive-since-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Recurring deductions and auto-charges stop generating for weeks starting on or after this date. Past settlements remain untouched.
                    </p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="make">Make</Label>
                    <Input
                      id="make"
                      value={formData.make}
                      onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                      placeholder="e.g., Freightliner"
                      data-testid="unit-make-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="model">Model</Label>
                    <Input
                      id="model"
                      value={formData.model}
                      onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                      placeholder="e.g., Cascadia"
                      data-testid="unit-model-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="year">Year</Label>
                    <Input
                      id="year"
                      type="number"
                      value={formData.year}
                      onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                      placeholder="e.g., 2023"
                      data-testid="unit-year-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="license_plate">License Plate</Label>
                    <Input
                      id="license_plate"
                      value={formData.license_plate}
                      onChange={(e) => setFormData({ ...formData, license_plate: e.target.value })}
                      placeholder="e.g., ABC-1234"
                      data-testid="unit-plate-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vin">VIN</Label>
                  <Input
                    id="vin"
                    value={formData.vin}
                    onChange={(e) => setFormData({ ...formData, vin: e.target.value })}
                    placeholder="Vehicle Identification Number"
                    data-testid="unit-vin-input"
                  />
                </div>
                {/* Dispatch Fee Configuration */}
                <div className="space-y-2 p-3 rounded-lg border bg-muted/30">
                  <Label className="text-sm font-medium">Dispatch Fee</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <Select
                      value={formData.dispatch_fee_type}
                      onValueChange={(value) => setFormData({ ...formData, dispatch_fee_type: value, dispatch_fee_value: value === 'default' ? '' : formData.dispatch_fee_value })}
                    >
                      <SelectTrigger data-testid="unit-fee-type-select">
                        <SelectValue placeholder="Fee type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="default">Company Default (10%)</SelectItem>
                        <SelectItem value="percentage">Custom Percentage</SelectItem>
                        <SelectItem value="flat">Flat Fee (per load)</SelectItem>
                        <SelectItem value="weekly_flat">Weekly Flat Fee (per week)</SelectItem>
                      </SelectContent>
                    </Select>
                    {formData.dispatch_fee_type !== 'default' && (
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.dispatch_fee_value}
                        onChange={(e) => setFormData({ ...formData, dispatch_fee_value: e.target.value })}
                        placeholder={formData.dispatch_fee_type === 'percentage' ? 'e.g., 10' : 'e.g., 35.00'}
                        data-testid="unit-fee-value-input"
                      />
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {formData.dispatch_fee_type === 'default' && 'Uses company default (10% of gross payout)'}
                    {formData.dispatch_fee_type === 'percentage' && `${formData.dispatch_fee_value || '0'}% of each load's gross payout`}
                    {formData.dispatch_fee_type === 'flat' && `$${formData.dispatch_fee_value || '0'} flat fee per load`}
                    {formData.dispatch_fee_type === 'weekly_flat' && `$${formData.dispatch_fee_value || '0'} flat fee per WEEK (charged once per week, regardless of how many loads)`}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="driver">Assign Driver</Label>
                  <Select
                    value={formData.assigned_driver_id}
                    onValueChange={(value) => setFormData({ ...formData, assigned_driver_id: value })}
                  >
                    <SelectTrigger data-testid="unit-driver-select">
                      <SelectValue placeholder="Select driver (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <User className="h-4 w-4" />
                          No driver assigned
                        </div>
                      </SelectItem>
                      {getAvailableDrivers().length > 0 ? (
                        getAvailableDrivers().map((driver) => (
                          <SelectItem key={driver.id} value={driver.id}>
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4 text-blue-600" />
                              <span>{driver.name}</span>
                              {driver.phone && (
                                <span className="text-xs text-muted-foreground">({driver.phone})</span>
                              )}
                            </div>
                          </SelectItem>
                        ))
                      ) : (
                        <div className="px-2 py-3 text-sm text-muted-foreground text-center">
                          <AlertCircle className="h-4 w-4 mx-auto mb-1" />
                          No available drivers
                        </div>
                      )}
                      {/* Show currently assigned driver if editing */}
                      {editingUnit?.assigned_driver_id && (
                        drivers
                          .filter(d => d.id === editingUnit.assigned_driver_id)
                          .map((driver) => (
                            <SelectItem key={driver.id} value={driver.id}>
                              <div className="flex items-center gap-2">
                                <User className="h-4 w-4 text-green-600" />
                                <span>{driver.name}</span>
                                <Badge variant="outline" className="text-xs">Current</Badge>
                              </div>
                            </SelectItem>
                          ))
                      )}
                    </SelectContent>
                  </Select>
                  {drivers.length === 0 && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      No drivers created yet. Add drivers first.
                    </p>
                  )}
                </div>

                {/* Dispatcher */}
                <div className="space-y-2">
                  <Label htmlFor="dispatcher">Assign Dispatcher</Label>
                  <Select
                    value={formData.assigned_dispatcher_id}
                    onValueChange={(value) => setFormData({ ...formData, assigned_dispatcher_id: value })}
                  >
                    <SelectTrigger data-testid="unit-dispatcher-select">
                      <SelectValue placeholder="Select dispatcher (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">
                        <span className="text-muted-foreground">No dispatcher assigned</span>
                      </SelectItem>
                      {dispatchers.filter((d) => d.status === 'active').map((d) => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.name}
                          <span className="text-xs text-muted-foreground ml-1">({d.pay_rate || 0}%)</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {dispatchers.length === 0 && (
                    <p className="text-xs text-muted-foreground">
                      No dispatchers yet. Add one in <a className="underline text-blue-600" href="/dispatchers">People → Dispatchers</a>.
                    </p>
                  )}
                </div>

                {/* Owner Operator */}
                <div className="space-y-2">
                  <Label htmlFor="oo">Assign Owner-Operator</Label>
                  <Select
                    value={formData.assigned_owner_operator_id}
                    onValueChange={(value) => setFormData({ ...formData, assigned_owner_operator_id: value })}
                  >
                    <SelectTrigger data-testid="unit-owner-op-select">
                      <SelectValue placeholder="Select owner-operator (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">
                        <span className="text-muted-foreground">No owner-operator assigned</span>
                      </SelectItem>
                      {ownerOps.filter((o) => o.status === 'active' || o.id === formData.assigned_owner_operator_id).map((o) => (
                        <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="unit-submit-btn">
                    {editingUnit ? 'Update' : 'Create'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Search + Status Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search units..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="units-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[170px]" data-testid="units-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active only</SelectItem>
                <SelectItem value="inactive">Inactive only</SelectItem>
                <SelectItem value="all">All units</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Unit #</TableHead>
                <TableHead>Vehicle</TableHead>
                <TableHead>License Plate</TableHead>
                <TableHead>Assigned Driver</TableHead>
                <TableHead>Dispatch Fee</TableHead>
                <TableHead>Status</TableHead>
                {canEdit && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUnits.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canEdit ? 7 : 6} className="text-center py-8 text-muted-foreground">
                    {units.length === 0 ? (
                      <div className="flex flex-col items-center gap-2">
                        <Truck className="h-8 w-8 text-muted-foreground/50" />
                        <p>No units yet. Add your first truck!</p>
                      </div>
                    ) : (
                      'No units match your search'
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                filteredUnits.map((unit) => (
                  <TableRow key={unit.id} data-testid={`unit-row-${unit.id}`}>
                    <TableCell className="font-medium">{unit.unit_number}</TableCell>
                    <TableCell>
                      {unit.year} {unit.make} {unit.model}
                    </TableCell>
                    <TableCell>{unit.license_plate || '-'}</TableCell>
                    <TableCell>
                      {canEdit ? (
                        <Select
                          value={unit.assigned_driver_id || 'none'}
                          onValueChange={(value) => handleQuickAssign(unit.id, value)}
                        >
                          <SelectTrigger className="w-[180px] h-8" data-testid={`quick-assign-${unit.id}`}>
                            <SelectValue>
                              {unit.assigned_driver_name ? (
                                <div className="flex items-center gap-2">
                                  <User className="h-3 w-3 text-green-600" />
                                  <span className="truncate">{unit.assigned_driver_name}</span>
                                </div>
                              ) : (
                                <span className="text-muted-foreground">Assign driver...</span>
                              )}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">
                              <span className="text-muted-foreground">Unassign</span>
                            </SelectItem>
                            {drivers.filter(d => d.status === 'active' || d.id === unit.assigned_driver_id).map((driver) => (
                              <SelectItem key={driver.id} value={driver.id}>
                                <div className="flex items-center gap-2">
                                  <User className="h-3 w-3" />
                                  {driver.name}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span>{unit.assigned_driver_name || '-'}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {unit.dispatch_fee_type ? (
                        <span className="text-xs font-medium" data-testid={`unit-fee-display-${unit.id}`}>
                          {unit.dispatch_fee_type === 'percentage'
                            ? `${unit.dispatch_fee_value}%`
                            : unit.dispatch_fee_type === 'weekly_flat'
                              ? `$${unit.dispatch_fee_value?.toFixed(2)}/week`
                              : `$${unit.dispatch_fee_value?.toFixed(2)}/load`}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">Default (10%)</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={statusColors[unit.status]}>
                      {unit.status}
                    </Badge>
                    {unit.status !== 'active' && unit.inactive_since && (
                      <div className="text-[10px] text-muted-foreground mt-0.5">since {unit.inactive_since}</div>
                    )}
                    </TableCell>
                    {canEdit && (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(unit)}
                            data-testid={`edit-unit-${unit.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {unit.status === 'active' ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSetUnitStatus(unit, 'inactive')}
                              className="text-slate-500 hover:text-slate-700"
                              data-testid={`deactivate-unit-${unit.id}`}
                            >
                              Deactivate
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSetUnitStatus(unit, 'active')}
                              className="border-blue-300 text-blue-700 hover:bg-blue-50"
                              data-testid={`reactivate-unit-${unit.id}`}
                            >
                              <RotateCcw className="h-3.5 w-3.5 mr-1" />
                              Reactivate
                            </Button>
                          )}
                          {isAdmin && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(unit.id)}
                              className="text-red-600 hover:text-red-700"
                              data-testid={`delete-unit-${unit.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="trailers" className="space-y-6">
          <TrailersTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
